import { EyeOff, Eye } from "lucide-react";
import Navbar from "../../components/layout/Navbar";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/Button";
import { useState, FormEvent } from "react";
import { LoginResponse, useLoginMutation } from "../../services/authApi";
import { useDispatch } from "react-redux";
import { setCredentials } from "../../features/authSlice";
import { GoogleLoginButton } from "../../components/GoogleAuth/GoogleAuth";
import { toast } from "sonner";

export const loginRedirectLogic = (
  navigate: (x: string) => void,
  userData: LoginResponse
) => {
  // Safely get search parameters
  let redirectUrl = "";
  let slug = "";

  try {
    const searchParams = new URLSearchParams(window.location.search);
    redirectUrl = searchParams.get("redirect") || "";
    slug = searchParams.get("slug") || "";
  } catch (urlError) {
    console.warn("Error parsing URL search parameters:", urlError);
    // Continue without redirect parameters
  }

  // Handle redirect logic
  if (redirectUrl && redirectUrl.trim()) {
    try {
      const redirectUrlObj = new URL(redirectUrl);
      const isPreviewEnv = redirectUrlObj.hostname.startsWith("pr");

      const slugs = userData.user.sites
        .map((site) => {
          // Safely extract slug, handle cases where site might be null/undefined
          return site && typeof site === "object" ? site.slug : null;
        })
        .filter(Boolean); // Remove null/undefined values

      if (isPreviewEnv || (slug && slugs.includes(slug))) {
        if (slug) {
          try {
            redirectUrlObj.searchParams.set("slug", slug);
            redirectUrlObj.searchParams.set("access_token", userData.access);
            window.location.href = redirectUrlObj.toString();
            return;
          } catch (redirectError) {
            console.error("Error setting redirect parameters:", redirectError);
            // Fall through to default navigation
          }
        }
      }
    } catch (urlError) {
      console.error("Error parsing redirect URL:", urlError);
      // Invalid redirect URL, continue with default navigation
    }
  }

  if (userData.user.sites.length < 1) {
    navigate("/add-store");
  } else {
    navigate("/my-stores");
  }
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [login, { isLoading }] = useLoginMutation();

  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const userData = await login(formData).unwrap();
      // Store user data and token in Redux state
      // console.log("userData", userData.user.sites.length);
      dispatch(
        setCredentials({
          user: userData.user,
          token: userData.access,
        })
      );
      loginRedirectLogic(navigate, userData);
    } catch (error: unknown) {
      if (typeof error === "object" && error !== null && "data" in error) {
        const err = error as { data?: Record<string, string[]> };
        toast.error(
          Object.entries(err.data as Record<string, string[]>)
            .map(([_, value]) => value)
            .join(", ") || "Login failed: Unknown Error"
        );
      }
      console.error("Login failed:", error);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="p-5 sm:p-14 flex items-center justify-center landing-bg bg-cover bg-no-repeat bg-center">
        <div className="w-full md:w-fit flex items-center justify-center flex-col bg-white">
          <div className=" sm:p-10 rounded-2xl sm:border border-[#7E57C233]  w-[100%] sm:w-[80%] md:w-fit flex items-center justify-between gap-20 ">
            <div className="flex flex-col gap-3 items-start justify-start w-[500px]">
              <div>
                <h2 className="">Welcome Back!</h2>
                <span>
                  Don't have an account?{" "}
                  <Link to="/signup">
                    <span className=" underline text-[#7E57C2] font-medium">
                      Sign up
                    </span>
                  </Link>
                </span>
              </div>

              <div className="flex flex-col items-center justify-center mt-2 w-full">
                <GoogleLoginButton />
              </div>
              <p className="text-center w-full -mb-2">OR</p>

              <form
                className="flex flex-col items-start justify-center gap-8 w-full"
                onSubmit={handleSubmit}
              >
                <div className="flex items-start justify-center gap-6 flex-col w-full">
                  <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                    <label htmlFor="email" className="text-[#666666CC]">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      name="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="h-11 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                      required
                    />
                  </div>
                  <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                    <div className="flex items-center justify-between w-full">
                      <label htmlFor="password" className="text-[#666666CC]">
                        Password
                      </label>
                    </div>
                    <div className="w-full relative">
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2  text-sm"
                        onClick={() => setShowPassword((prev) => !prev)}
                        tabIndex={-1}
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? <EyeOff /> : <Eye />}
                      </button>
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        name="password"
                        id="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="h-11 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                        required
                      />
                    </div>
                  </div>
                  <div className=" flex items-center justify-between w-full -mt-3">
                    <div className="flex items-center justify-center gap-2 outline-0">
                      <input
                        type="checkbox"
                        name="rememberMe"
                        id="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleChange}
                        className=" accent-[#7E57C2]"
                      />
                      <span>Remember Me</span>
                    </div>
                    <Link to="/reset-password" className="text-[#7E57C2]">
                      Forgot Password?
                    </Link>
                  </div>
                </div>
                <div className="w-full flex items-center justify-between">
                  <Link
                    to="/signup"
                    className="underline text-[#7E57C2] font-medium hidden sm:block"
                  >
                    <span>Sign Up instead</span>
                  </Link>
                  <Button
                    text={isLoading ? "Logging In..." : "Log in"}
                    disabled={isLoading}
                    className="w-full sm:w-fit"
                  />
                </div>
              </form>
            </div>
            <img
              src="/assets/Jellosite-Logo-09.jpg"
              alt=""
              className="w-[250px] hidden md:block"
            />
          </div>
          <ul className="text-[#666666] md:w-full hidden sm:flex gap-3.5 sm:w-[80%]  items-center justify-end mt-1 ">
            <Link to="">
              <li>Help</li>
            </Link>
            <Link to="/privacy_policy">
              <li>Privacy</li>
            </Link>
            <Link to="/terms_conditions">
              <li>Terms</li>
            </Link>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Login;
